import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Game Schemas - Zod definitions for all game types
 * These provide runtime validation and TypeScript type inference
 */

// Base game fields shared by all sport types
const baseGameFields = {
  _id: objectId,
  date: z.coerce.date().default(() => new Date()),
  globalDate: z.string().optional(),
  time: z.string().optional(),
  timeSlot: objectId,
  duration: z.number().default(60), // duration in minutes
  isBye: z.boolean().optional(),
  organization: z.string(), // Required ObjectId
  league: z.string(), // Required ObjectId
  division: z.string(), // Required ObjectId
  locationModel: objectId,
  location: z.string().optional(),
  gameLabel: z.string().optional(),
  playoffRound: z.enum(['quarterfinal', 'consolation_semifinal', 'semifinal', 'championship', 'third_place', 'fifth_place', 'seventh_place', 'consolation', '-', '']).nullable().optional(),
  isCancelled: z.boolean().default(false),
  isUpdated: z.boolean().default(false),
  isHidden: z.boolean().default(false),
  // Display-only text shown while a bracket slot's team is undecided,
  // e.g. "Winner of Quarterfinal 1". Cleared/overridden by advancement.
  homeTeamPlaceholder: z.string().optional(),
  awayTeamPlaceholder: z.string().optional(),
  competitionMeta: z.object({
    pool: z.number().optional(),
    round: z.number().optional(),
    gameNumber: z.number().optional(),
    stage: z.enum(['championship', 'third_place', 'quarterfinal', 'semifinal', 'consolation_semifinal', 'fifth_place', 'seventh_place']).optional(),
    // Canonical unique key for a single playoff bracket slot, e.g. 'championship',
    // 'semifinal-1', 'quarterfinal-3'. Maps 1:1 to the scheduler's DOM data-slot-id.
    // Legacy games have only `stage` (championship/third_place) — consumers fall back
    // to `bracketSlot ?? stage`, so those still resolve into the Final / 3rd-place slots.
    bracketSlot: z.string().optional(),
  }).optional(),
  ...timestampFields,
};

// Metadata for base game fields
export const baseGameMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  date: { label: 'Date', editable: true },
  globalDate: { label: 'Global Date', hidden: true },
  time: { label: 'Time', editable: true },
  timeSlot: { ref: 'TimeSlot', label: 'Time Slot' },
  duration: { label: 'Duration', editable: true },
  isBye: { label: 'Is Bye', editable: true },
  organization: { ref: 'Organization', hidden: true },
  league: { ref: 'League', hidden: true },
  division: { ref: 'Division', hidden: true },
  locationModel: { ref: 'Location', label: 'Location Model' },
  location: { label: 'Location', editable: true },
  gameLabel: { label: 'Game Label', editable: true },
  playoffRound: { label: 'Playoff Round', editable: true },
  isCancelled: { label: 'Cancelled', editable: true },
  isUpdated: { label: 'Updated', hidden: true },
  isHidden: { label: 'Hidden', hidden: true },
  homeTeamPlaceholder: { label: 'Home Team Placeholder', editable: true },
  awayTeamPlaceholder: { label: 'Away Team Placeholder', editable: true },
  competitionMeta: { label: 'Competition Meta', hidden: true },
  created: { hidden: true },
  updated: { hidden: true },
};

// Basketball Game — the SHARED team-game schema. The physical `games`
// collection is built from this one schema (game.model.ts); volleyball and
// soccer games route here too (game.controller getGamesModelBySport default).
export const BasketballGameSchema = z.object({
  ...baseGameFields,
  sport: z.literal('basketball').default('basketball'),
  homeTeam: objectId,
  awayTeam: objectId,
  homeScore: z.number().optional(),
  awayScore: z.number().optional(),
  // Volleyball only: per-set points (e.g. [{home:25,away:20}, ...]). When set,
  // homeScore/awayScore store SETS WON (derived via
  // @staty/shared/utils/setScores.deriveSetsWon) so standings, brackets, and
  // playoff advancement keep reading the same two fields unchanged.
  setScores: z.array(z.object({ home: z.number(), away: z.number() })).optional(),
});

export const basketballGameMetadata: Record<string, FieldMetadata> = {
  ...baseGameMetadata,
  homeTeam: { ref: 'DivisionTeam', label: 'Home Team' },
  awayTeam: { ref: 'DivisionTeam', label: 'Away Team' },
  homeScore: { label: 'Home Score', headerName: 'Home', editable: true, width: 80 },
  awayScore: { label: 'Away Score', headerName: 'Away', editable: true, width: 80 },
  // Owned by the volleyball score-entry UI, not the generic grid.
  setScores: { label: 'Set Scores', hidden: true },
};

export type BasketballGame = z.infer<typeof BasketballGameSchema>;

// Judo Game
export const JudoGameSchema = z.object({
  ...baseGameFields,
  sport: z.literal('judo').default('judo'),
  winner: objectId,
  gender: z.enum(['male', 'female']).default('male'),
  round: z.string().optional(),
});

export const judoGameMetadata: Record<string, FieldMetadata> = {
  ...baseGameMetadata,
  winner: { ref: 'Player', label: 'Winner' },
  gender: { label: 'Gender', editable: true },
  round: { label: 'Round', editable: true },
};

export type JudoGame = z.infer<typeof JudoGameSchema>;

// Baseball Game
export const BaseballGameSchema = z.object({
  ...baseGameFields,
  sport: z.literal('baseball').default('baseball'),
  homeTeam: objectId,
  awayTeam: objectId,
  homeScore: z.number().optional(),
  awayScore: z.number().optional(),
});

export const baseballGameMetadata: Record<string, FieldMetadata> = {
  ...baseGameMetadata,
  homeTeam: { ref: 'DivisionTeam', label: 'Home Team' },
  awayTeam: { ref: 'DivisionTeam', label: 'Away Team' },
  homeScore: { label: 'Home Score', editable: true, width: 80 },
  awayScore: { label: 'Away Score', editable: true, width: 80 },
};

export type BaseballGame = z.infer<typeof BaseballGameSchema>;

// Golf Game
export const GolfGameSchema = z.object({
  ...baseGameFields,
  sport: z.literal('golf').default('golf'),
  format: z.enum(['stroke', 'match', 'stableford']).default('stroke'),
});

export const golfGameMetadata: Record<string, FieldMetadata> = {
  ...baseGameMetadata,
  format: { label: 'Format', editable: true },
};

export type GolfGame = z.infer<typeof GolfGameSchema>;

// Racket Game (Tennis, Badminton, etc.)
export const RacketGameSchema = z.object({
  ...baseGameFields,
  sport: z.literal('racket').default('racket'),
  format: z.enum(['best-of-1', 'best-of-3', 'best-of-5']).default('best-of-3'),
});

export const racketGameMetadata: Record<string, FieldMetadata> = {
  ...baseGameMetadata,
};

export type RacketGame = z.infer<typeof RacketGameSchema>;

// Track Game
export const TrackGameSchema = z.object({
  ...baseGameFields,
  sport: z.literal('track').default('track'),
  eventName: z.string().optional(), // e.g. "100m Sprint", "Long Jump"
  resultType: z.enum(['time', 'distance', 'points']).optional(),
  gender: z.enum(['male', 'female', 'mixed']).default('mixed'),
  isRelay: z.boolean().default(false),
  participants: z.array(z.string()).default([]),
});

export const trackGameMetadata: Record<string, FieldMetadata> = {
  ...baseGameMetadata,
  participants: { ref: 'Player', label: 'Participants' },
};

export type TrackGame = z.infer<typeof TrackGameSchema>;

// Union type for all games
export type Game = BasketballGame | JudoGame | BaseballGame | GolfGame | RacketGame | TrackGame;

// Export all schemas
export const GameSchemas = {
  basketball: BasketballGameSchema,
  judo: JudoGameSchema,
  baseball: BaseballGameSchema,
  golf: GolfGameSchema,
  racket: RacketGameSchema,
  track: TrackGameSchema,
};
